// ─── questionnaireVerrouillage (cockpit) ─────────────────────────────────
//
// Chantier 9b. Côté cockpit, deux choses seulement :
//   · LIRE l'état du pré-questionnaire d'une cliente (état, date d'envoi,
//     demande de correction en attente) ;
//   · AUTORISER une correction — UNE seule nouvelle transmission.
//
// POURQUOI LA LECTURE VA DIRECTEMENT EN BASE, sans passer par la fiche locale.
// Le magasin local du cockpit (src/store.js) ne connaît pas ces colonnes : il
// pousse une liste blanche de champs et ne les relit jamais. Tant mieux — il
// ne peut donc pas les écraser. Mais cela veut dire que la fiche en mémoire
// n'a rien à dire sur le verrou : un verrou qu'on affiche depuis un cache
// vieux de quelques minutes est un verrou qu'on affiche faux.
//
// L'AUTORISATION EST NOMINATIVE. L'acteur inscrit au journal est l'adresse de
// la session Supabase en cours, pas une valeur passée par l'appelant : une
// signature qu'on peut se choisir soi-même ne signe rien.

import { supabase, isCloudEnabled, getCurrentUser } from '../supabaseClient';

/** Les colonnes du verrou. Même liste que côté serverless (api/_questionnaire-verrouillage.js). */
const COLONNES_VERROU = [
  'questionnaire_transmis_le',
  'questionnaire_correction_jeton',
  'questionnaire_correction_autorisee_le',
  'questionnaire_correction_autorisee_par',
  'questionnaire_correction_demandee_le',
].join(', ');

/**
 * L'ancre qui mène au bloc du verrou dans la fiche.
 *
 * Elle est posée par la cloche de notifications avant d'ouvrir la fiche, et
 * lue par le bloc au montage, qui l'efface ensuite. Une intention de
 * navigation ne doit pas survivre à l'écran qui l'a servie : sans effacement,
 * un simple rechargement replongerait Anissa sur ce bloc sans raison.
 */
export const ANCRE_VERROU_QUESTIONNAIRE = 'verrou-questionnaire';

export const ETATS = {
  NON_TRANSMIS: 'non_transmis',
  VERROUILLE: 'verrouille',
  CORRECTION_AUTORISEE: 'correction_autorisee',
};

/**
 * Traduit une ligne de la base en état affichable. Fonction PURE : c'est elle
 * que les tests verrouillent, pas le rendu.
 *
 * @param {object|null} ligne
 * @returns {{etat: string, transmisLe: string|null, autoriseeLe: string|null,
 *            autoriseePar: string|null, demandeeLe: string|null,
 *            peutAutoriser: boolean}}
 */
export function lireVerrou(ligne) {
  const transmisLe = ligne?.questionnaire_transmis_le ?? null;
  const jeton = ligne?.questionnaire_correction_jeton ?? null;

  let etat = ETATS.NON_TRANSMIS;
  if (transmisLe && jeton) etat = ETATS.CORRECTION_AUTORISEE;
  else if (transmisLe) etat = ETATS.VERROUILLE;

  return {
    etat,
    transmisLe,
    autoriseeLe: ligne?.questionnaire_correction_autorisee_le ?? null,
    autoriseePar: ligne?.questionnaire_correction_autorisee_par ?? null,
    demandeeLe: ligne?.questionnaire_correction_demandee_le ?? null,
    // Autoriser n'a de sens qu'une fois le questionnaire transmis, et pas deux
    // fois de suite : un second jeton écraserait le premier sans qu'on sache
    // lequel a servi.
    peutAutoriser: !!transmisLe && !jeton,
  };
}

/** Libellé de l'état, pour l'écran d'Anissa. */
export function libelleEtat(etat) {
  if (etat === ETATS.CORRECTION_AUTORISEE) return 'Correction autorisée';
  if (etat === ETATS.VERROUILLE) return 'Transmis · verrouillé';
  return 'Pas encore transmis';
}

/**
 * Lit l'état du verrou d'une cliente.
 *
 * @returns {Promise<{ok: true, verrou: object} | {ok: false, erreur: string}>}
 */
export async function lireEtatQuestionnaire(clientId) {
  if (!isCloudEnabled) return { ok: false, erreur: 'hors_ligne' };
  if (!clientId) return { ok: false, erreur: 'client_absent' };

  const { data, error } = await supabase
    .from('clients')
    .select(COLONNES_VERROU)
    .eq('id', clientId)
    .maybeSingle();

  if (error) return { ok: false, erreur: error.message };
  if (!data) return { ok: false, erreur: 'introuvable' };
  return { ok: true, verrou: lireVerrou(data) };
}

/**
 * Autorise UNE nouvelle transmission.
 *
 * L'écriture est CONDITIONNELLE : elle ne s'applique que si la fiche est
 * toujours transmise et toujours sans jeton. Deux onglets du cockpit, ou deux
 * clics rapides, ne produisent donc qu'une seule autorisation — la seconde ne
 * touche aucune ligne et le dit.
 *
 * Le verrou se repose tout seul : c'est la transmission qui efface le jeton,
 * dans le même ordre SQL que les réponses (api/save-questionnaire.js).
 *
 * @returns {Promise<{ok: true, verrou: object} | {ok: false, erreur: string}>}
 */
export async function autoriserCorrection(clientId) {
  if (!isCloudEnabled) return { ok: false, erreur: 'hors_ligne' };
  if (!clientId) return { ok: false, erreur: 'client_absent' };

  const utilisateur = await getCurrentUser();
  const acteur = utilisateur?.email || null;
  if (!acteur) {
    // Sans identité, l'autorisation ne serait attribuable à personne. On
    // préfère refuser : un journal qui dit « quelqu'un » ne répond pas à la
    // question à laquelle il est censé répondre.
    return { ok: false, erreur: 'session_absente' };
  }

  const maintenant = new Date().toISOString();
  const jeton = crypto.randomUUID();

  const { data, error } = await supabase
    .from('clients')
    .update({
      questionnaire_correction_jeton: jeton,
      questionnaire_correction_autorisee_le: maintenant,
      questionnaire_correction_autorisee_par: acteur,
    })
    .eq('id', clientId)
    .not('questionnaire_transmis_le', 'is', null)
    .is('questionnaire_correction_jeton', null)
    .select(COLONNES_VERROU);

  if (error) return { ok: false, erreur: error.message };
  if (!data || data.length === 0) {
    // Soit le questionnaire n'a jamais été transmis, soit une autorisation
    // était déjà en cours. Dans les deux cas il n'y a rien à faire, et l'écran
    // doit relire l'état plutôt que d'inventer le sien.
    return { ok: false, erreur: 'sans_objet' };
  }

  // Journal : qui a autorisé, et quand. Aucun contenu médical, jamais.
  const { error: journalErr } = await supabase.from('questionnaire_journal').insert({
    client_id: clientId,
    evenement: 'autorisation_correction',
    acteur,
  });
  if (journalErr) {
    // L'autorisation est posée ; c'est sa trace qui manque. On le dit fort
    // plutôt que de faire croire à un échec — la cliente peut déjà corriger.
    // eslint-disable-next-line no-console
    console.error(`[questionnaire] autorisation non journalisee : ${journalErr.message}`);
  }

  return { ok: true, verrou: lireVerrou(data[0]) };
}
